import { api } from '../configs/apiConfig'; // Adjust the import path as necessary

const ORG_UNIT_ENDPOINT = '/organizationalunit';

class OrganizationalUnitService {
    // Get all organizational units
    async getAllUnits() {
        try {
            const response = await api.get(ORG_UNIT_ENDPOINT);
            return response.data;
        } catch (error) {
            console.error('Error fetching organizational units:', error);
            throw error;
        }
    }

    // Get organizational unit by ID
    async getUnitById(id) {
        try {
            const response = await api.get(`${ORG_UNIT_ENDPOINT}/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching organizational unit with ID ${id}:`, error);
            throw error;
        }
    }

    // Add a new organizational unit
    async addUnit(unitData) {
        try {
            const response = await api.post(ORG_UNIT_ENDPOINT, unitData);
            return response.data;
        } catch (error) {
            console.error('Error adding organizational unit:', error);
            throw error;
        }
    }

    // Update an existing organizational unit
    async updateUnit(id, unitData) {
        try {
            await api.put(`${ORG_UNIT_ENDPOINT}/${id}`, unitData);
        } catch (error) {
            console.error(`Error updating organizational unit with ID ${id}:`, error);
            throw error;
        }
    }

    // Delete an organizational unit
    async deleteUnit(id) {
        try {
            await api.delete(`${ORG_UNIT_ENDPOINT}/${id}`);
        } catch (error) {
            console.error(`Error deleting organizational unit with ID ${id}:`, error);
            throw error;
        }
    }

    // Get organizational unit types
    async getUnitTypes() {
        try {
            const response = await api.get(`${ORG_UNIT_ENDPOINT}/types`);
            return response.data;
        } catch (error) {
            console.error('Error fetching organizational unit types:', error);
            throw error;
        }
    }

    // Get the organization chart
    async getOrganizationChart() {
        try {
            const response = await api.get(`${ORG_UNIT_ENDPOINT}/chart`);
            return response.data;
        } catch (error) {
            console.error('Error fetching organization chart:', error);
            throw error;
        }
    }
    
    // Get roles for a specific organizational unit
    async getRolesForUnit(unitId) {
        try {
            const response = await api.get(`${ORG_UNIT_ENDPOINT}/${unitId}/roles`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching roles for unit with ID ${unitId}:`, error);
            throw error;
        }
    }
    
    // Get all parent units for a specific unit
    async getParentUnits(unitId) {
        try {
            const response = await api.get(`${ORG_UNIT_ENDPOINT}/parents/${unitId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching parent units for unit with ID ${unitId}:`, error);
            throw error;
        }
    }
    
    // Get all units a user belongs to
    async getUserUnits(userId) {
        try {
            const response = await api.get(`${ORG_UNIT_ENDPOINT}/user/${userId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching units for user with ID ${userId}:`, error);
            throw error;
        }
    }
    
    // Helper method to get unit name by ID
    async getUnitNameById(unitId) {
        try {
            const unit = await this.getUnitById(unitId);
            return unit ? unit.name : null;
        } catch (error) {
            console.error(`Error getting name for unit with ID ${unitId}:`, error);
            throw error;
        }
    }
    
    // Helper method to get unit ID by name
    async getUnitIdByName(unitName) {
        try {
            const units = await this.getAllUnits();
            const unit = units.find(u => u.name === unitName);
            return unit ? unit.id : null;
        } catch (error) {
            console.error(`Error getting ID for unit with name ${unitName}:`, error);
            throw error;
        }
    }

    // Get all users in a specific organizational unit
    async getUsersByUnitId(unitId) {
        try {
            const response = await api.get(`${ORG_UNIT_ENDPOINT}/${unitId}/users`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching users for unit with ID ${unitId}:`, error);
            throw error;
        }
    }

    // Get all subordinate users under a specific user based on organizational hierarchy
    async getSubordinateUsers(userId) {
        try {
            const response = await api.get(`${ORG_UNIT_ENDPOINT}/user/${userId}/subordinates`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching subordinate users for user with ID ${userId}:`, error);
            throw error;
        }
    }
}

export default new OrganizationalUnitService();